import { Box, IconButton, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { colors, CustomPagination } from '../../components';
import ButtonComponent from '../../components/button/ButtonComponent';
import { AutopayInfoWrapper } from '../../components/global-styles/GlobalStyles';
import AutoPayAuthorizationModal from '../../components/modals/AutoPayAuthorizationModal';
import CommonTabs from '../../components/tabComponent/commonTabComponent';
import { TextFieldComponent } from '../../components/text-field';
import CommonHeader from '../../components/ui-components/CommonHeader';
import { useContracts } from '../../hooks/useContracts';
import { AssetApiResponse, getContractAssets } from '../../services/assets-service';
import { ContractApiResponse } from '../../services/contract-services';
import { getDocumentsList } from '../../services/documents-service';
import { getOpenInvoices, OpenInvoiceApiResponse } from '../../services/payment-services';
import { RootState } from '../../store';
import { hideLoader, showLoader } from '../../store/reducers/appSlice';
import { openToast } from '../../store/reducers/toastSlice';
import {
    trackAutoPayModalOpen,
    trackContractTabViewed,
    trackDocumentSearchUsed,
    trackMakePaymentCtaClick,
    trackPaymentHistorySearchUsed,
} from '../../utils/google-analytics';
import { formatCurrency, formatToUSDate, shouldDisablePayNow } from '../../utils/helper';
import { NavigationConfig } from '../../utils/navigation-constants';
import { useTranslation } from '../../utils/translations';

import AmortizationSchedule from './amortizationSchedule';
import AssetsTab from './assests';
import { AddBankAccountDrawer } from './components/AddBankAccountDrawer';
import { ContractHeaderActions } from './components/ContractHeaderActions';
import { ContractSummaryCards } from './components/ContractSummaryCards';
import { ExportDropdownMenu } from './components/ExportDropdownMenu';
import { LoanProgressCard } from './components/LoanProgressCard';
import { PdfPreviewModal } from './components/PdfPreviewModal';
import { getFormType } from './contractDetailsHelpers';
import DocumentsTab, { ContractDocumentApiItem } from './documents';
import { useAddBankAccountForm } from './hooks/useAddBankAccountForm';
import { useAmortizationSchedule } from './hooks/useAmortizationSchedule';
import { useAutoPayAndPdfPreview } from './hooks/useAutoPayAndPdfPreview';
import { usePaymentHistoryExport } from './hooks/usePaymentHistoryExport';
import OverView from './overView';
import PaymentHistory from './paymentHistory';

const ContractDetails: React.FC = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md'));
    const isNarrowMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isDesktopRow = useMediaQuery(theme.breakpoints.up('lg'));
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { contracts: activeContracts, fetchContracts } = useContracts();
    const impersonation = useSelector((state: RootState) => state.impersonation);
    const isImpersonating = Boolean(impersonation?.isImpersonating);
    const [contract, setContract] = useState<ContractApiResponse | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState(0);
    const showAutoPaySuspendedBanner =
        contract?.AutoPay && contract?.AutoPayResumeDate && dayjs(contract.AutoPayResumeDate).isAfter(dayjs(), 'day');

    // Payment History Pagination State (controlled from parent)
    const [paymentCurrentPage, setPaymentCurrentPage] = useState<number>(0);
    const [paymentRowsPerPage, setPaymentRowsPerPage] = useState<number>(10);
    const [paymentHistorySearchTerm, setPaymentHistorySearchTerm] = useState('');
    const [paymentHistoryFilteredCount, setPaymentHistoryFilteredCount] = useState<number>(-1);
    const [paymentHistoryTotalAmount, setPaymentHistoryTotalAmount] = useState<number>(0);

    // Documents State
    const [documentsData, setDocumentsData] = useState<ContractDocumentApiItem[]>([]);
    const [documentsLoading, setDocumentsLoading] = useState(false);

    // Documents Pagination State (controlled from parent)
    const [documentsCurrentPage, setDocumentsCurrentPage] = useState<number>(0);
    const [documentsRowsPerPage, setDocumentsRowsPerPage] = useState<number>(5);
    const [documentsSearchTerm, setDocumentsSearchTerm] = useState('');
    const [documentsFilteredCount, setDocumentsFilteredCount] = useState<number>(-1);
    // Assets State
    const [assetsData, setAssetsData] = useState<AssetApiResponse[]>([]);
    const [assetsLoading, setAssetsLoading] = useState(false);

    // Open Invoices State
    const [openInvoices, setOpenInvoices] = useState<OpenInvoiceApiResponse[]>([]);
    const initialOpenInvoicesFetch = useRef(false);

    const isConsumer = useSelector((state: RootState) => state.user.userDetails?.AccountType) === 'I';

    const {
        paymentHistoryData,
        paymentHistoryLoading,
        fetchPaymentHistory,
        exportPaymentHistoryAnchorEl,
        handleExportPaymentHistoryMenu,
        handleCloseExportPaymentHistoryMenu,
        handleExportPaymentHistoryFormatSelect,
    } = usePaymentHistoryExport(contract, isImpersonating);

    const {
        amortizationData,
        amortizationLoading,
        fetchAmortizationSchedule,
        exportAnchorEl,
        handlePrintAmortizationSchedule,
        handleExportAmortizationSchedule,
        handleCloseExportMenu,
        handleExportFormatSelect,
    } = useAmortizationSchedule(contract, isImpersonating);

    const {
        autoPayModalOpen,
        setAutoPayModalOpen,
        handleAutoPaySubmit,
        pdfPreviewOpen,
        pdfPreviewUrl,
        handleClosePdfPreview,
        handleDownloadPDF,
    } = useAutoPayAndPdfPreview(contract, isImpersonating, setContract);

    const {
        addBankDrawerOpen,
        addBankCountry,
        setAddBankCountry,
        addBankFormData,
        addBankFormErrors,
        setAddBankFormErrors,
        showAddBankAccountNumber,
        setShowAddBankAccountNumber,
        showAddBankReEnterAccountNumber,
        setShowAddBankReEnterAccountNumber,
        showAddBankRoutingNumber,
        setShowAddBankRoutingNumber,
        showAddBankReEnterRoutingNumber,
        setShowAddBankReEnterRoutingNumber,
        handleAddBankInputChange,
        handleCloseAddBankDrawer,
        handleAddBankAccountSubmit,
        openDrawerForAutoPayFallback,
    } = useAddBankAccountForm({
        onAccountAdded: shouldReopenAutoPay => {
            if (shouldReopenAutoPay) {
                setAutoPayModalOpen(true);
            }
        },
    });

    // Fetch Documents
    const fetchDocuments = useCallback(async () => {
        if (!contract?.Id) return;
        setDocumentsLoading(true);
        try {
            const response = await getDocumentsList(contract.Id.toString(), true);
            if (response.statusCode === 200) {
                const documentsData: ContractDocumentApiItem[] = response?.data || [];
                setDocumentsData(documentsData);
            }
        } catch (err) {
            console.error('Error fetching documents:', err);
        } finally {
            setDocumentsLoading(false);
            dispatch(hideLoader());
        }
    }, [contract?.Id, dispatch]);

    // Reset pagination when tab changes (no API calls, restore initial data)
    const handleTabChange = (index: number) => {
        setActiveTab(index);
        setPaymentCurrentPage(0);
        setDocumentsCurrentPage(0);
        handleCloseExportMenu();

        const tabName = tabItems[index]?.label || `tab_${index}`;
        trackContractTabViewed(tabName, contract?.AspireId || String(contract?.Id ?? ''));
    };

    // Payment History Pagination Handlers
    const handlePaymentPageChange = (newPage: number, newRowsPerPage: number) => {
        setPaymentCurrentPage(newPage);
        setPaymentRowsPerPage(newRowsPerPage);
    };

    const handlePaymentRowsPerPageChange = (newRowsPerPage: number) => {
        setPaymentCurrentPage(0);
        setPaymentRowsPerPage(newRowsPerPage);
    };

    // Documents Pagination Handlers
    const handleDocumentsPageChange = (newPage: number, newRowsPerPage: number) => {
        setDocumentsCurrentPage(newPage);
        setDocumentsRowsPerPage(newRowsPerPage);
    };

    const handleDocumentsRowsPerPageChange = (newRowsPerPage: number) => {
        setDocumentsCurrentPage(0);
        setDocumentsRowsPerPage(newRowsPerPage);
    };

    // Reset to first page when tab search term changes
    useEffect(() => {
        setPaymentCurrentPage(0);
    }, [paymentHistorySearchTerm]);

    useEffect(() => {
        setDocumentsCurrentPage(0);
    }, [documentsSearchTerm]);

    // Track search usage, debounced so we only fire once the user pauses typing
    useEffect(() => {
        if (!paymentHistorySearchTerm.trim()) return undefined;
        const timer = setTimeout(() => {
            trackPaymentHistorySearchUsed(
                contract?.AspireId || String(contract?.Id ?? ''),
                paymentHistoryFilteredCount !== 0,
            );
        }, 600);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paymentHistorySearchTerm]);

    useEffect(() => {
        if (!documentsSearchTerm.trim()) return undefined;
        const timer = setTimeout(() => {
            trackDocumentSearchUsed(contract?.AspireId || String(contract?.Id ?? ''), documentsFilteredCount !== 0);
        }, 600);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [documentsSearchTerm]);

    useEffect(() => {
        const contractData = (location.state as { contract?: ContractApiResponse })?.contract;

        if (contractData && contractData.Id) {
            // Use contract data directly from navigation state
            setContract(contractData);
        } else {
            // Fallback: if no contract data in state, try to get from all contracts
            const fetchContractDetails = async () => {
                try {
                    setError(null);
                    const allContracts = await fetchContracts(false);
                    if (allContracts.data && allContracts.data.length > 0) {
                        setContract(allContracts.data[0]);
                    } else {
                        const errorMessage = allContracts.message || 'No Contract Data Available';
                        setError(errorMessage);
                        dispatch(
                            openToast({
                                show: true,
                                isAlertOpen: true,
                                message: errorMessage,
                                severity: 'error',
                            }),
                        );
                    }
                } catch (err) {
                    console.error('Error fetching contract details:', err);
                    const e = err as { message?: string; response?: { data?: { message?: string } } };
                    const errorMessage =
                        e?.response?.data?.message ||
                        e?.message ||
                        'Failed to Load Contract Details. Please Try Again.';
                    setError(errorMessage);
                    dispatch(
                        openToast({
                            show: true,
                            isAlertOpen: true,
                            message: errorMessage,
                            severity: 'error',
                        }),
                    );
                } finally {
                    dispatch(hideLoader());
                }
            };
            fetchContractDetails();
        }
    }, [location.state, dispatch, fetchContracts]);

    // Auto-open autopay modal if coming from AutoPayLink
    useEffect(() => {
        const shouldOpenAutoPay = (location.state as { openAutoPayModal?: boolean })?.openAutoPayModal;
        if (shouldOpenAutoPay && contract?.Id) {
            setAutoPayModalOpen(true);
            // Clear the state to prevent reopening on refresh
            window.history.replaceState({}, document.title, window.location.href);
        }
    }, [contract?.Id, location.state, setAutoPayModalOpen]);

    // Fetch Assets
    const fetchAssetsList = useCallback(async () => {
        if (!contract?.Id) return;
        setAssetsLoading(true);
        try {
            const response = await getContractAssets(contract.Id, true);
            if (response.statusCode === 200 && Array.isArray(response.data)) {
                setAssetsData(response.data);
            } else {
                setAssetsData([]);
            }
        } catch (err) {
            console.error('Error fetching assets:', err);
            setAssetsData([]);
        } finally {
            setAssetsLoading(false);
            dispatch(hideLoader());
        }
    }, [contract?.Id, dispatch]);

    // Fetch Open Invoices
    const fetchOpenInvoicesData = async () => {
        try {
            const response = await getOpenInvoices(false);
            if (response.statusCode === 200 && Array.isArray(response.data)) {
                setOpenInvoices(response.data);
            }
        } catch (err) {
            console.error('Error fetching open invoices:', err);
        }
    };

    // Fetch initial data when contract is available (only once)
    useEffect(() => {
        if (contract?.Id) {
            // Fetch all data for payment history (client-side pagination)
            fetchPaymentHistory();
            fetchAmortizationSchedule();
            fetchAssetsList();
            fetchDocuments();
        }
    }, [contract?.Id, fetchPaymentHistory, fetchAmortizationSchedule, fetchAssetsList, fetchDocuments]);

    // Fetch open invoices on component mount
    useEffect(() => {
        if (initialOpenInvoicesFetch.current) return;
        initialOpenInvoicesFetch.current = true;

        fetchOpenInvoicesData();
    }, []);

    const tabItems = contract
        ? [
              {
                  label: t('contracts.tabs.overview'),
                  content: <OverView contract={contract} />,
              },
              {
                  label: t('contracts.tabs.paymentHistory'),
                  content: (
                      <>
                          {isMobileOrTablet && (
                              <Stack
                                  direction="row"
                                  flexWrap="wrap"
                                  alignItems="center"
                                  gap={1}
                                  px={{ xs: 1.5, sm: 3 }}
                                  pb={1.25}
                              >
                                  <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
                                      <TextFieldComponent
                                          value={paymentHistorySearchTerm}
                                          onChange={e => setPaymentHistorySearchTerm(e.target.value)}
                                          placeholder={
                                              t('contracts.paymentHistory.searchPlaceholder') ||
                                              'Search payment history'
                                          }
                                          ariaLabel={
                                              t('contracts.paymentHistory.searchPlaceholder') ||
                                              'Search payment history'
                                          }
                                          startIcon={
                                              <span
                                                  className="icon-search"
                                                  style={{ fontSize: 16, color: 'inherit' }}
                                              />
                                          }
                                          endIcon={
                                              paymentHistorySearchTerm ? (
                                                  <IconButton
                                                      size="small"
                                                      onClick={() => setPaymentHistorySearchTerm('')}
                                                      aria-label={t('payments.filter.clearSearch') || 'Clear search'}
                                                      sx={{ p: 0.5 }}
                                                  >
                                                      <span className="icon-close" style={{ fontSize: 16 }} />
                                                  </IconButton>
                                              ) : undefined
                                          }
                                          sx={{ width: '100%' }}
                                          className="searchTextFiled smallTextFiled"
                                      />
                                  </Box>
                                  {!isImpersonating && paymentHistoryData?.length > 0 && (
                                      <ButtonComponent
                                          variant="outlined"
                                          onClick={handleExportPaymentHistoryMenu}
                                          endIcon={<span className="icon-arrow-drop-down" />}
                                          MDButton={true}
                                          fullWidth={false}
                                          sx={{ width: 'fit-content', flexShrink: 0, whiteSpace: 'nowrap' }}
                                      >
                                          {t('common.Export') || 'Export'}
                                      </ButtonComponent>
                                  )}
                              </Stack>
                          )}
                          {isMobileOrTablet && paymentHistoryData?.length > 0 && (
                              <Box mx={{ xs: 1.5, sm: 3 }} mb={1.25}>
                                  <Stack
                                      direction="row"
                                      justifyContent="space-between"
                                      flexWrap="wrap"
                                      gap={0.5}
                                      px={1.5}
                                      py={1}
                                      sx={{
                                          bgcolor: colors.greyPreset[5],
                                          borderRadius: 2,
                                      }}
                                  >
                                      <Typography variant="body2" color="textSecondary">
                                          Filtered:{' '}
                                          <Typography component="span" variant="body2" fontWeight={700}>
                                              {(paymentHistoryFilteredCount >= 0
                                                  ? paymentHistoryFilteredCount
                                                  : paymentHistoryData.length) || 0}{' '}
                                              batches
                                          </Typography>
                                      </Typography>
                                      <Typography variant="body2" color="textSecondary">
                                          Total Processed:{' '}
                                          <Typography component="span" variant="body2" fontWeight={700}>
                                              {formatCurrency(paymentHistoryTotalAmount)}
                                          </Typography>
                                      </Typography>
                                  </Stack>
                              </Box>
                          )}
                          <PaymentHistory
                              status={contract.Status === 1}
                              contractId={contract.Id}
                              data={paymentHistoryData}
                              loading={paymentHistoryLoading}
                              currentPage={paymentCurrentPage}
                              rowsPerPage={paymentRowsPerPage}
                              searchTerm={paymentHistorySearchTerm}
                              onSearchChange={setPaymentHistorySearchTerm}
                              onTotalCountChange={setPaymentHistoryFilteredCount}
                              onTotalAmountChange={setPaymentHistoryTotalAmount}
                          />
                      </>
                  ),
              },
              // Show Amortization Schedule only if contract type is Loan AND contract status is Active
              ...(contract.IsLoan && contract.Status === 1
                  ? [
                        {
                            label: t('contracts.tabs.amortizationSchedule'),
                            content: (
                                <AmortizationSchedule
                                    contractId={contract.Id}
                                    data={amortizationData}
                                    loading={amortizationLoading}
                                    Yield={contract.Yield}
                                    onExportClick={!isImpersonating ? handleExportAmortizationSchedule : undefined}
                                    onPrintClick={!isImpersonating ? handlePrintAmortizationSchedule : undefined}
                                />
                            ),
                        },
                    ]
                  : []),
              {
                  label: t('contracts.tabs.documents'),
                  content: (
                      <>
                          {isMobileOrTablet && (
                              <Box px={{ xs: 1.5, sm: 3 }} pb={1.5}>
                                  <TextFieldComponent
                                      value={documentsSearchTerm}
                                      onChange={e => setDocumentsSearchTerm(e.target.value)}
                                      placeholder={
                                          t('contracts.documents.searchPlaceholder') || 'Search by name, date, type...'
                                      }
                                      ariaLabel={
                                          t('contracts.documents.searchPlaceholder') || 'Search by name, date, type...'
                                      }
                                      startIcon={
                                          <span className="icon-search" style={{ fontSize: 16, color: 'inherit' }} />
                                      }
                                      endIcon={
                                          documentsSearchTerm ? (
                                              <IconButton
                                                  size="small"
                                                  onClick={() => setDocumentsSearchTerm('')}
                                                  aria-label={t('payments.filter.clearSearch') || 'Clear search'}
                                                  sx={{ p: 0.5 }}
                                              >
                                                  <span className="icon-close" style={{ fontSize: 16 }} />
                                              </IconButton>
                                          ) : undefined
                                      }
                                      sx={{ width: '100%' }}
                                      className="searchTextFiled smallTextFiled"
                                  />
                              </Box>
                          )}
                          <DocumentsTab
                              contractId={contract.Id}
                              data={documentsData}
                              loading={documentsLoading}
                              currentPage={documentsCurrentPage}
                              rowsPerPage={documentsRowsPerPage}
                              searchTerm={documentsSearchTerm}
                              onSearchChange={setDocumentsSearchTerm}
                              onTotalCountChange={setDocumentsFilteredCount}
                          />
                      </>
                  ),
              },
              {
                  label: t('contracts.tabs.Assets'),
                  content: <AssetsTab contractId={contract.Id} data={assetsData} loading={assetsLoading} />,
              },
          ]
        : [];

    const goBack = () => {
        navigate(NavigationConfig.ContractOverview);
    };

    // Find Payment History tab index
    const paymentHistoryTabIndex = tabItems.findIndex(tab => tab.label === t('contracts.tabs.paymentHistory'));

    // Find Amortization Schedule tab index
    const amortizationScheduleTabIndex = tabItems.findIndex(
        tab => tab.label === t('contracts.tabs.amortizationSchedule'),
    );

    // Find Documents tab index
    const documentsTabIndex = tabItems.findIndex(tab => tab.label === t('contracts.tabs.documents'));

    if (error || !contract) {
        return null;
    }

    const isActive = contract.Status === 1;
    // Calculate payment progress percentage based on PrincipleAmount and OutstandingBalance
    // const principleAmount = Number(contract.PrincipleAmount) || 0;
    // const outstandingBalance = Number(contract.OutstandingBalance) || 0;
    // const totalPaid = principleAmount - outstandingBalance;
    // const paymentPercentage = principleAmount > 0 ? (totalPaid / principleAmount) * 100 : 0;
    const paymentPercentage =
        contract.PaymentsReceived > 0 ? (contract.PaymentsReceived / contract.NumberOfPayments) * 100 : 0;

    const isAmortizationScheduleTab =
        activeTab === amortizationScheduleTabIndex &&
        amortizationScheduleTabIndex !== -1 &&
        amortizationData?.length > 0;

    // Pre-compute Pay Now button props to avoid IIFEs inside JSX
    const isConsumerLoan = contract.AccountType === 'I' && contract.IsLoan;
    const disabledByAutopay = shouldDisablePayNow(contract.AspireId, activeContracts);
    const hasOutstandingBalance = contract.OutstandingBalance && Number(contract.OutstandingBalance) > 0;
    const hasPaymentEligibility = openInvoices.length > 0 || hasOutstandingBalance;
    const payNowDisabled = isConsumerLoan ? disabledByAutopay : !hasPaymentEligibility || disabledByAutopay;
    let payNowTooltip = '';
    if (payNowDisabled) {
        if (disabledByAutopay) {
            payNowTooltip =
                t('contracts.details.payNowDisabledAutopay') ||
                'Payment is managed by AutoPay. Manual payments are not available.';
        } else if (!hasPaymentEligibility && !isConsumerLoan) {
            payNowTooltip = t('contracts.details.payNowNoOpenInvoices') || 'No open invoices available for payment.';
        }
    }

    // Pre-compute CommonTabs toolbar props to avoid deep nested ternaries in JSX
    // On mobile/tablet, Payment History's search + export render inside the tab content
    // (below the tabs) instead of in the CommonTabs header, so CommonTabs is skipped for it there.
    const isPaymentHistoryTab = activeTab === paymentHistoryTabIndex && paymentHistoryTabIndex !== -1;
    const isDocumentsTab = activeTab === documentsTabIndex && documentsTabIndex !== -1;
    const showPaymentHistoryExportInHeader = isPaymentHistoryTab && paymentHistoryData?.length > 0 && !isMobileOrTablet;
    // On mobile/tablet, Amortization's Export/Print render inside its own card header
    // (see amortizationSchedule.tsx) instead of the shared CommonTabs header, to match its mobile design.
    const showAmortizationHeaderControls = isAmortizationScheduleTab && !isMobileOrTablet;
    const showExportBtn = !isImpersonating && (showPaymentHistoryExportInHeader || showAmortizationHeaderControls);
    const tabPrimaryContent = showExportBtn ? t('common.Export') || 'Export' : undefined;
    const tabPrimaryIconClass =
        showAmortizationHeaderControls || showPaymentHistoryExportInHeader ? 'icon-arrow-drop-down' : 'icon-upload';
    const tabPrimaryClick =
        !isImpersonating && showPaymentHistoryExportInHeader
            ? handleExportPaymentHistoryMenu
            : showAmortizationHeaderControls
              ? handleExportAmortizationSchedule
              : undefined;
    const tabSecondaryContent =
        !isImpersonating && showAmortizationHeaderControls ? t('common.Print') || 'Print' : undefined;
    const tabSecondaryClick = showAmortizationHeaderControls ? handlePrintAmortizationSchedule : undefined;

    const payHistoryEndIcon = paymentHistorySearchTerm ? (
        <IconButton
            size="small"
            onClick={() => setPaymentHistorySearchTerm('')}
            aria-label={t('payments.filter.clearSearch') || 'Clear search'}
            sx={{ p: 0.5 }}
        >
            <span className="icon-close" style={{ fontSize: 16 }} />
        </IconButton>
    ) : undefined;
    const docsEndIcon = documentsSearchTerm ? (
        <IconButton
            size="small"
            onClick={() => setDocumentsSearchTerm('')}
            aria-label={t('payments.filter.clearSearch') || 'Clear search'}
            sx={{ p: 0.5 }}
        >
            <span className="icon-close" style={{ fontSize: 16 }} />
        </IconButton>
    ) : undefined;
    let tabTrailingContent: React.ReactNode;
    if (isPaymentHistoryTab && !isMobileOrTablet) {
        tabTrailingContent = (
            <Box
                sx={{
                    width: isNarrowMobile ? '100%' : 280,
                    flex: isNarrowMobile || isDesktopRow ? 'unset' : '1 1 200px',
                    minWidth: 0,
                }}
            >
                <TextFieldComponent
                    value={paymentHistorySearchTerm}
                    onChange={e => setPaymentHistorySearchTerm(e.target.value)}
                    placeholder={t('contracts.paymentHistory.searchPlaceholder') || 'Search by date, amount, method...'}
                    ariaLabel={
                        t('contracts.paymentHistory.searchPlaceholder') ||
                        'Search payment history by date, amount, method'
                    }
                    startIcon={<span className="icon-search" style={{ fontSize: 16, color: 'inherit' }} />}
                    endIcon={payHistoryEndIcon}
                    sx={{ width: '100%' }}
                    className="searchTextFiled smallTextFiled"
                />
            </Box>
        );
    } else if (isDocumentsTab && !isMobileOrTablet) {
        tabTrailingContent = (
            <Box sx={{ width: 280 }}>
                <TextFieldComponent
                    value={documentsSearchTerm}
                    onChange={e => setDocumentsSearchTerm(e.target.value)}
                    placeholder={t('contracts.documents.searchPlaceholder') || 'Search by name, date, type...'}
                    startIcon={<span className="icon-search" style={{ fontSize: 16, color: 'inherit' }} />}
                    endIcon={docsEndIcon}
                    sx={{ width: '100%' }}
                    className="searchTextFiled smallTextFiled"
                />
            </Box>
        );
    }

    return (
        <Stack gap={3}>
            {/* Header Section */}
            <Stack
                direction={'row'}
                justifyContent={'space-between'}
                flexWrap={'wrap'}
                gap={2}
                alignItems={'center'}
                px={3}
                pt={3}
                sx={
                    isMobileOrTablet
                        ? {
                              position: 'sticky',
                              top: 0,
                              zIndex: 2,
                              bgcolor: colors.fullWhite,
                          }
                        : undefined
                }
            >
                <CommonHeader
                    icon="icon-arrow-left"
                    cursorPointer="pointer"
                    iconBgColor={colors.fullWhite}
                    mainTitle={contract.AspireId}
                    subTitle={assetsData[0]?.AssetDesc ?? ''}
                    onclick={goBack}
                    iconColor={colors.primaryLight[100]}
                    borderColor={colors.primaryLight[100]}
                    tabIndex={0}
                />
                {contract.Status === 1 && (
                    <ContractHeaderActions
                        contract={contract}
                        payNowDisabled={payNowDisabled}
                        payNowTooltip={payNowTooltip}
                        onEnableAutoPayClick={() => {
                            setAutoPayModalOpen(true);
                            trackAutoPayModalOpen(contract?.AspireId || String(contract?.Id));
                        }}
                        onPayNowClick={() => {
                            trackMakePaymentCtaClick(
                                contract?.AspireId || String(contract?.Id ?? ''),
                                'contract_details',
                            );
                            dispatch(showLoader());
                            navigate(NavigationConfig.DashboardMakePayment, {
                                state: {
                                    selectedContractIds: [contract.AspireId],
                                    fromContracts: true,
                                },
                            });
                        }}
                    />
                )}
            </Stack>
            <Box>
                <Stack spacing={3}>
                    <Stack spacing={3} px={3}>
                        {/* Summary Cards */}
                        <ContractSummaryCards contract={contract} />
                        {showAutoPaySuspendedBanner && (
                            <AutopayInfoWrapper direction="row" alignItems="center">
                                <>
                                    <span className="icon-info-circle" />
                                    <Typography variant="subtitle2">
                                        {t('common.AutopayInfoNoteStart')}{' '}
                                        {contract?.AutoPayResumeDate
                                            ? formatToUSDate(contract.AutoPayResumeDate)
                                            : 'N/A'}{' '}
                                        {t('common.AutopayInfoNoteEnd')}
                                    </Typography>
                                </>
                            </AutopayInfoWrapper>
                        )}
                        {/* Contract Details Section */}
                        <LoanProgressCard
                            contract={contract}
                            assetsData={assetsData}
                            isActive={isActive}
                            paymentPercentage={paymentPercentage}
                        />
                    </Stack>
                </Stack>

                {/* Tabs Section - Flexible */}
                <Stack>
                    <Box
                        sx={{
                            flex: 1,
                            '& .tabHeader': {
                                mx: { xs: 1.5, sm: 3 },
                                mt: { xs: 3, lg: 0 },
                            },
                        }}
                    >
                        {/* Search: only Payment History & Documents. Export: only Payment History & Amortization Schedule. */}
                        <CommonTabs
                            tabs={tabItems}
                            defaultActiveIndex={0}
                            activeIndex={activeTab}
                            onTabChange={handleTabChange}
                            primaryBtnContent={tabPrimaryContent}
                            primaryBtnIcon={
                                <span className={tabPrimaryIconClass} style={{ fontSize: 14, fontWeight: 600 }} />
                            }
                            onPrimaryBtnClick={tabPrimaryClick}
                            secondaryBtnContent={tabSecondaryContent}
                            onSecondaryBtnClick={tabSecondaryClick}
                            isAmortizationScheduleTab={isAmortizationScheduleTab}
                            trailingContent={tabTrailingContent}
                        />
                    </Box>
                </Stack>

                {/* Modals */}
                <AutoPayAuthorizationModal
                    open={autoPayModalOpen}
                    onClose={() => setAutoPayModalOpen(false)}
                    onSubmit={handleAutoPaySubmit}
                    formType={contract ? getFormType(contract, isConsumer) : 'consumer-lease'}
                    agreementNumber={contract?.AspireId || ''}
                    isImpersonating={isImpersonating}
                    onAddBankAccountRequest={() => {
                        setAutoPayModalOpen(false);
                        openDrawerForAutoPayFallback();
                    }}
                />

                <AddBankAccountDrawer
                    open={addBankDrawerOpen}
                    onClose={handleCloseAddBankDrawer}
                    country={addBankCountry}
                    onCountryChange={value => {
                        setAddBankCountry(value);
                        if (addBankFormErrors.country) {
                            setAddBankFormErrors(prev => ({ ...prev, country: '' }));
                        }
                    }}
                    formData={addBankFormData}
                    formErrors={addBankFormErrors}
                    onFieldChange={handleAddBankInputChange}
                    onSubmit={handleAddBankAccountSubmit}
                    showAccountNumber={showAddBankAccountNumber}
                    onToggleAccountNumberVisibility={setShowAddBankAccountNumber}
                    showReEnterAccountNumber={showAddBankReEnterAccountNumber}
                    onToggleReEnterAccountNumberVisibility={setShowAddBankReEnterAccountNumber}
                    showRoutingNumber={showAddBankRoutingNumber}
                    onToggleRoutingNumberVisibility={setShowAddBankRoutingNumber}
                    showReEnterRoutingNumber={showAddBankReEnterRoutingNumber}
                    onToggleReEnterRoutingNumberVisibility={setShowAddBankReEnterRoutingNumber}
                />

                {/* PDF Preview Modal */}
                <PdfPreviewModal
                    open={pdfPreviewOpen}
                    pdfPreviewUrl={pdfPreviewUrl}
                    onClose={handleClosePdfPreview}
                    onDownload={handleDownloadPDF}
                />

                {/* Export Dropdown Menu - Attached to Export button */}
                <ExportDropdownMenu
                    anchorEl={exportAnchorEl}
                    onClose={handleCloseExportMenu}
                    onSelectFormat={handleExportFormatSelect}
                    ariaLabel="Amortization schedule export options"
                />

                {/* Export Dropdown Menu for Payment History - Attached to Payment History Export button */}
                <ExportDropdownMenu
                    anchorEl={exportPaymentHistoryAnchorEl}
                    onClose={handleCloseExportPaymentHistoryMenu}
                    onSelectFormat={handleExportPaymentHistoryFormatSelect}
                    ariaLabel="Payment history export options"
                />
            </Box>

            {/* Pagination Sections */}
            <Stack>
                {activeTab === paymentHistoryTabIndex &&
                    paymentHistoryTabIndex !== -1 &&
                    paymentHistoryData?.length > 0 && (
                        <CustomPagination
                            totalCount={
                                paymentHistoryFilteredCount >= 0
                                    ? paymentHistoryFilteredCount
                                    : paymentHistoryData.length
                            }
                            rowsPerPage={paymentRowsPerPage}
                            currentPage={paymentCurrentPage}
                            onPageChange={handlePaymentPageChange}
                            onRowsPerPageChange={handlePaymentRowsPerPageChange}
                            serverSide={false}
                            sortedRowsLength={
                                paymentHistoryFilteredCount >= 0
                                    ? paymentHistoryFilteredCount
                                    : paymentHistoryData.length
                            }
                            sx={{
                                [theme.breakpoints.down('md')]: {
                                    justifyContent: 'space-between',
                                    px: 1.5,
                                    '& .MuiTablePagination-toolbar': { paddingLeft: 0, paddingRight: 0 },
                                },
                            }}
                        />
                    )}

                {/* Documents Pagination - Only show when Documents tab is active */}
                {activeTab === documentsTabIndex && documentsTabIndex !== -1 && documentsData?.length > 0 && (
                    <CustomPagination
                        totalCount={documentsFilteredCount >= 0 ? documentsFilteredCount : documentsData.length}
                        rowsPerPage={documentsRowsPerPage}
                        currentPage={documentsCurrentPage}
                        onPageChange={handleDocumentsPageChange}
                        onRowsPerPageChange={handleDocumentsRowsPerPageChange}
                        serverSide={false}
                        sortedRowsLength={documentsFilteredCount >= 0 ? documentsFilteredCount : documentsData.length}
                    />
                )}
            </Stack>
        </Stack>
    );
};

export default ContractDetails;
